import React from "react";
import { Box, Typography, Card, CardContent, Grid, Chip, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface DashboardSectionProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  linkTo?: string;
  children: React.ReactNode;
  actionItems?: Array<{
    label: string;
    count: number;
    color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
    onClick?: () => void;
  }>;
}

const DashboardSection: React.FC<DashboardSectionProps> = ({ title, icon, color, linkTo, children, actionItems = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: `4px solid ${color}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": linkTo
          ? {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
            }
          : {},
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2.5 }}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          sx={{ cursor: linkTo ? "pointer" : "default" }}
          onClick={handleClick}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
              {title}
            </Typography>
          </Box>
          {actionItems.length > 0 && (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {actionItems.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.label}: ${item.count}`}
                  size="small"
                  color={item.count > 0 ? item.color : "default"}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.onClick) {
                      item.onClick();
                    }
                  }}
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: item.count > 0 ? 600 : 400,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1 }}>{children}</Box>
      </CardContent>
    </Card>
  );
};

export default DashboardSection;
